import { makeStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import { Button, NoSsr } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { Link as RouterLink } from 'react-router-dom';
import { CatalogueProduct } from '../../../api/products/catalogue';
import { isAuthenticatedState, showPricesState } from '../../../api/appState';
import { activeFavoriteProducts, showStockAvailability } from '../../../config';
import { isMasterProduct } from '../../../helpers/isMasterProduct';
import { isProductCanceled } from '../../../helpers/isProductCanceled';
import useProductUrl from '../../../hooks/useProductUrl';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailFavoriteToggle from '../../ProductDetailPage/components/ProductDetailFavoriteToggle';
import ProductDetailAlternatives from '../../ProductDetailPage/components/ProductDetailAlternatives';
import CataloguePageListContentItemCardAddToCart from './CataloguePageListContentItemCardAddToCart';
import CataloguePageListContentItemCardDescription from './CataloguePageListContentItemCardDescription';
import CataloguePageListContentItemCardImage from './CataloguePageListContentItemCardImage';
import CataloguePageListContentItemCardInlineParameters from './CataloguePageListContentItemCardInlineParameters';
import CataloguePageListContentItemCardName from './CataloguePageListContentItemCardName';
import CataloguePageListContentItemCardPackageInfo from './CataloguePageListContentItemCardPackageInfo';
import CataloguePageListContentItemCardPrice from './CataloguePageListContentItemCardPrice';
import CataloguePageListContentItemCardStockInfo from './CataloguePageListContentItemCardStockInfo';
import CataloguePageListContentItemWarning from './CataloguePageListContentItemWarning';

export interface CataloguePageListContentItemCardProps
  extends StandardDivProps<CataloguePageListContentItemCardClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemCardClassKey = 'root';

const themeSettings = { name: 'CataloguePageListContentItemCard' };
const useStyles = makeStyles(
  (theme) => ({
    root: {
      gridGap: 0,
      display: 'grid',
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
      backgroundColor: 'transparent',
      flexBasis: '100%',
      flexGrow: 0,
      position: 'relative',
      flexShrink: 1,
      gridTemplateAreas: `
      "image        image"
      "name         name"
      "description  description"
      "parameters   parameters"
      "price        stockInfo"
      "price        addToCart"
      `,
      gridTemplateRows: 'auto 1fr 40px 30px 20px 55px',
      [theme.breakpoints.down('sm')]: {
        gridTemplateRows: 'auto 1fr 0 30px 20px 55px',
      },
      gridTemplateColumns: '1fr 1fr',
      padding: theme.spacing(1),
      '&:hover $favoriteToggle': {
        opacity: 1,
      },
      [theme.breakpoints.down('xs')]: {
        gridTemplateAreas: `
        "image        image"
        "name         name"
        "description  description"
        "parameters   parameters"
        "price        price"
        "stockInfo    stockInfo"
        "addToCart    addToCart"
        `,
        gridTemplateRows: 'auto 1fr 0 30px auto auto auto',
        // Stack elements vertically
      },
    },
    canceledProductRoot: {
      gridTemplateAreas: `
      "code         code"
      "image        image"
      "name         name"
      "alternatives alternatives"
      "parameters   parameters"
      "variants     variants"
      `,
      gridTemplateRows: '20px auto 78px 96px 20px auto',
    },
    image: {
      gridArea: 'image',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1 / 2,
      },
      margin: 'auto',
      width: '100%',
      maxWidth: '100%',
    },
    productName: {
      gridArea: 'name',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 2,
        '-ms-grid-column': 1 / 2,
      },
    },
    description: {
      gridArea: 'description',
      paddingTop: theme.spacing(1),
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 4,
        '-ms-grid-column': 1 / 2,
      },
    },
    price: {
      gridArea: 'price',
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'flex-start',
      },
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 6,
        '-ms-grid-column': 1,
      },
    },
    // code: {
    //   gridArea: 'code',
    //   ...theme.typography.caption,
    //   color: theme.palette.text.secondary,
    //   '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
    //     '-ms-grid-row': 1,
    //     '-ms-grid-column': 1,
    //   },
    // },
    // codeLable: {
    //   paddingRight: '1ch',
    //   color: theme.palette.text.hint,
    // },
    parameters: {
      gridArea: 'parameters',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 1,
        '-ms-grid-column': 1,
      },
    },
    addToCart: {
      gridArea: 'addToCart',
      justifyContent: 'flex-end',
    },
    stockInfo: {
      gridArea: 'stockInfo',
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'flex-start',
      },
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 5,
        '-ms-grid-column': 2,
      },
    },
    variants: {
      gridArea: 'price',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      // Fix grid in IE 10+
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '-ms-grid-row': 6,
        '-ms-grid-column': 1,
      },
    },
    packageInfo: {
      position: 'absolute',
      top: '0',
      right: '0',
      width: 'auto',
      padding: '1rem',
    },
    productClosedCross: {
      position: 'absolute',
      right: theme.spacing(1),
    },
    productDetailButton: {
      gridArea: 'addToCart',
      width: '100%',
      fontSize: '10px',
      marginTop: theme.spacing(1),
      height: theme.spacing(4),
      color: '#fff',
      borderRadius: '50px',
      border: '1px solid transparent',
      backgroundColor: '#1d1d1f',
      boxShadow: 'none',
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        backgroundColor: '#1d1d1f',
        color: '#fff',
        borderColor: 'transparent',
        boxShadow: '0 0 10px 4px rgba(0, 0, 0, 0.3)',
        transform: 'perspective(1px) scale(1.02)',
      },
    },
    favoriteToggle: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1,
      transition: 'opacity 0.3s ease',
      // backgroundColor: theme.palette.background.paper,
      // borderRadius: '50%',
    },
    alternatives: {
      gridArea: 'alternatives',
    },
  }),
  themeSettings,
);

const CataloguePageListContentItemCard = React.forwardRef<HTMLDivElement, CataloguePageListContentItemCardProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const isAuthenticated = useRecoilValue(isAuthenticatedState);
    const showPrices = useRecoilValue(showPricesState);
    const t = useTranslateWithFallback();
    const to = useProductUrl(product);

    const productIsCanceled = isProductCanceled(product.taxonomies);
    const isNotMasterProduct = !isMasterProduct(product.taxonomies);

    return (
      <div ref={ref} className={clsx(classes.root, { [classes.canceledProductRoot]: productIsCanceled }, className)}>
        <CataloguePageListContentItemCardImage className={classes.image} product={product} to={to} />
        {activeFavoriteProducts && isAuthenticated && isNotMasterProduct && (
          <NoSsr>
            <ProductDetailFavoriteToggle className={classes.favoriteToggle} productId={product.id} />
          </NoSsr>
        )}
        <CataloguePageListContentItemCardName className={classes.productName} product={product} />
        <CataloguePageListContentItemCardPackageInfo product={product} />
        <CataloguePageListContentItemCardDescription className={classes.description} product={product} />
        {/* <div className={classes.code}>
          <span className={classes.codeLable}>{t('common/code')}:</span>
          <span>{product.sku || ''}</span>
          {productIsCanceled && <CloseIcon color='error' fontSize='large' className={classes.productClosedCross} />}
        </div> */}
        <CataloguePageListContentItemCardInlineParameters className={classes.parameters} product={product} />
        {productIsCanceled === false ? (
          <>
            <CataloguePageListContentItemWarning className={classes.variants} product={product} type='grid' />
            {isNotMasterProduct && (
              <>
                {isAuthenticated && showPrices && (
                  <CataloguePageListContentItemCardPrice className={classes.price} product={product} />
                )}
                {showStockAvailability && isAuthenticated && (
                  <CataloguePageListContentItemCardStockInfo className={classes.stockInfo} product={product} />
                )}
              </>
            )}
            {isAuthenticated && isNotMasterProduct ? (
              <CataloguePageListContentItemCardAddToCart className={classes.addToCart} product={product} />
            ) : (
              <Button
                component={RouterLink}
                to={to}
                className={classes.productDetailButton}
                size='large'
                variant='contained'
              >
                {t('common/detail')}
              </Button>
            )}
          </>
        ) : (
          <ProductDetailAlternatives
            className={classes.alternatives}
            sectionName={t('productDetailPage/replacements')}
            productAlternatives={product.replacements}
            limit={1}
            productLink={to}
          />
        )}
      </div>
    );
  },
);

CataloguePageListContentItemCard.displayName = 'CataloguePageListContentItemCard';

export default withThemeProps(themeSettings)(CataloguePageListContentItemCard);
